import React from "react";
import { mean } from "d3-array";
import moment from "moment";
import {
  formatDataSimpleFromAPI,
  formatDataCombinedFromAPI,
  mergeDataSources,
  pickProperties
} from "../../../utils/popup";
import {
  filterArray,
  allCenters,
  dateNewNCEPSYNOP
} from "../../../utils/general";
import {
  fetchData,
  URL_SYNOP_AGGREGATED_OBSERVATIONS_POPUP,
  URL_SYNOP_AGGREGATED_OBSERVATIONS_TIMESERIES,
  URL_STATION
} from "../../../utils/api";
import LinkOSCAR from "../LinkOSCAR/LinkOSCAR";
import { check_tsi_wsi } from "./popupUtils";

export function fetchSynopTimelinessPopupInfo(
  props,
  report,
  periodsAsQP,
  multiplier,
  linkTimeSeries,
  setContent
) {
  fetchData(
    `${URL_SYNOP_AGGREGATED_OBSERVATIONS_POPUP}/?baseline=${props.baseline}&report=${report}&period_type=${props.selectedPeriodType}&station_id=${props.stationObject.station_id}&${periodsAsQP}&var_id=${props.selectedVariable}`
  )
    .then(response => {
      let returnedData = response.data;
      const in_oscar = props.stationObject.in_oscar
        ? props.stationObject.in_oscar
        : response.data[0].in_oscar;

      props.onSetSelectedStation({
        name: returnedData[0].station,
        wigosid: returnedData[0].wigosid,
        in_oscar,
        elevation: returnedData[0].elevation
      });

      if (
        props.selectedVariable === "110" &&
        props.selectedPeriodType !== "monthly"
      ) {
        returnedData = filterArray(returnedData, ["center"], "timeliness", {
          var_id: 1
        });
      }

      let formattedReturn;

      if (props.combinedMode) {
        formattedReturn = formatDataCombinedFromAPI(
          props,
          returnedData,
          props.selectedReport,
          in_oscar,
          multiplier
        );
      } else {
        formattedReturn = formatTimelinessDataSingleCenter(
          returnedData,
          formattedReturn
        );
      }

      let i = -1;
      let htmlContent = formattedReturn.data.map(d => {
        i += 1;
        if (d.name === "NWP-ID") {
          return check_tsi_wsi(d);
        }

        let value = d.value;
        return (
          <li key={d.name}>
            {d.name}: {value}
          </li>
        );
      });

      let links = linkTimeSeries;
      if (formattedReturn.oscar_id !== null) {
        links = (
          <>
            {linkTimeSeries}
            <br />
            <hr />
            <LinkOSCAR wigosid={formattedReturn.oscar_id} />
          </>
        );
      }
      setContent(
        <ul className="popup-station-properties">
          {htmlContent}
          {links}
        </ul>
      );
    })
    .catch(error => {
      console.log("ERROR API");
      console.log(error);
    });
}

function formatTimelinessDataSingleCenter(returnedData, formattedReturn) {
  let timelinessTemplate = [
    { name: "Name", value: "" },
    { name: "WIGOS-ID", value: null },
    { name: "NWP-ID", value: null },
    { name: "Timeliness", value: null }
  ];
  let arr = timelinessTemplate.map(a => ({ ...a }));
  arr[0].value = returnedData[0].station;
  if (arr[0].value === "Unknown") {
    arr[2].value = returnedData[0].wigosid;
  } else {
    arr[1].value = returnedData[0].wigosid;
  }
  let timelinessValue = returnedData[0].timeliness;
  if (timelinessValue === 0) {
    timelinessValue = "0m 00s";
  } else if (timelinessValue < 60) {
    timelinessValue = `0m ${Math.round(timelinessValue)}s`;
  } else {
    const minutes = Math.floor(timelinessValue / 60);
    const seconds = Math.round(timelinessValue % 60);
    timelinessValue = `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
  }
  arr[3].value = timelinessValue;
  let arrFiltered = arr.filter(e => e.value !== null);
  formattedReturn = { data: arrFiltered };
  return formattedReturn;
}

export function fetchSynopCombinedOrAvailabilityPopup(
  props,
  report,
  periodsAsQP,
  multiplier,
  linkTimeSeries,
  setContent,
  expectedGBON
) {
  const variable_point =
    props.selectedReport === "availability"
      ? props.stationObject.var_id
      : props.selectedVariable;
  fetchData(
    `${URL_SYNOP_AGGREGATED_OBSERVATIONS_POPUP}/?baseline=${props.baseline}&report=${report}&period_type=${props.selectedPeriodType}&station_id=${props.stationObject.station_id}&${periodsAsQP}&var_id=${variable_point}`
  )
    .then(response => {
      let returnedData = response.data;
      const in_oscar = props.stationObject.in_oscar
        ? props.stationObject.in_oscar
        : response.data[0].in_oscar;

      props.onSetSelectedStation({
        name: returnedData[0].station,
        wigosid: returnedData[0].wigosid,
        in_oscar,
        elevation: returnedData[0].elevation
      });

      if (
        props.combinedMode &&
        props.selectedReport === "availability" &&
        props.selectedVariable === "110" &&
        props.selectedPeriodType !== "monthly"
      ) {
        returnedData = filterArray(returnedData, ["center"], "nr_received", {
          var_id: 1
        });
      }
      let formattedReturn;

      if (props.combinedMode) {
        formattedReturn = formatDataCombinedFromAPI(
          props,
          returnedData,
          props.selectedReport,
          in_oscar,
          multiplier
        );
      } else {
        formattedReturn = formatDataSimpleFromAPI(props, returnedData);
      }

      if (
        !props.combinedMode &&
        props.fileType === "synop" &&
        props.selectedReport === "availability" &&
        props.stationObject.var_id === 1
      ) {
        formattedReturn.data.push({
          name: "Geopotential",
          value: "Geopotential"
        });
      }

      if (
        props.combinedMode &&
        props.fileType === "synop" &&
        props.selectedReport === "quality" &&
        props.selectedVariable === "58" &&
        moment.utc(props.selectedDate).format("YYYY-MM-DD") <
          moment.utc(dateNewNCEPSYNOP).format("YYYY-MM-DD")
      ) {
        formattedReturn.data.forEach((d, i) => {
          if (d.name === "#NCEP") {
            formattedReturn.data.splice(i, 1);
          }
        });
      }
      if (expectedGBON) {
        formattedReturn.data.forEach((d, i) => {
          if (
            props.selectedPeriodType === "monthly" &&
            props.selectedReport === "availability"
          ) {
            let centers = ["#DWD", "#ECMWF", "#JMA", "#NCEP"];
            if (centers.includes(d.name)) {
              formattedReturn.data[i] = {
                name: d.name,
                value: d.value
              };
            }
          }
          if (d.name === "#Expected") {
            formattedReturn.data[i] = expectedGBON;
          }
          if (d.name === "Default schedule") {
            formattedReturn.data.splice(i, 1);
          }
        });
      }

      let htmlContent;
      let max = 0;
      let maxIndex = 0;

      formattedReturn.data.forEach((d, index) => {
        if (
          props.selectedPeriodType === "monthly" &&
          props.selectedReport === "availability"
        ) {
          let centers = ["#DWD", "#ECMWF", "#JMA", "#NCEP"];

          if (centers.includes(d.name)) {
            if (d.value > max) {
              max = d.value;
              maxIndex = index;
            }

            formattedReturn.data[index] = {
              name: d.name,
              value: d.value
            };
          }
        }
        if (
          props.selectedPeriodType === "monthly" &&
          props.selectedReport === "quality"
        ) {
          let centers = ["#DWD", "#ECMWF", "#JMA", "#NCEP"];
          if (centers.includes(d.name)) {
            let rms = d.rms;
            if (props.selectedVariable === "58") {
              rms *= 100;
            }
            rms = (rms / 1).toFixed(2);
            let gross_error_percent = d.nr_gross_error / d.nr_received;
            if (isNaN(rms)) {
              rms = "No Data";
            }
            if (isNaN(gross_error_percent)) {
              gross_error_percent = "No data";
            }

            gross_error_percent = (gross_error_percent * 100).toFixed(2);
            let classElement = "popup-title-center";

            const toHighlight =
              Math.round(d.rms * 100000) / 100000 ===
              Math.round(props.stationObject.rms * 100000) / 100000;

            formattedReturn.data[index] = {
              name: <b>{d.name}</b>,
              value: (
                <ul key={d.name}>
                  <li key={d.name + "rms"}> RMSE: {rms}</li>
                  <li key={d.name + "ge"}> GE%: {gross_error_percent}</li>
                </ul>
              ),
              highlight: toHighlight
            };
          }
        }
        if (d.name === "#Expected") {
        }
      });

      let i = -1;
      htmlContent = formattedReturn.data.map(d => {
        i += 1;
        if (d.name === "NWP-ID") {
          return check_tsi_wsi(d);
        }

        if (d.name === "nr_received_note") {
          return (
            <li key={d.name}>
              <b>{d.value}</b>
            </li>
          );
        }
        let value = d.value;
        if (maxIndex === i) {
          value = <b>{d.value}</b>;
        } else if (d.highlight) {
          value = <b>{d.value}</b>;
        }
        return d.name === "Default schedule" || d.name === "Geopotential" ? (
          <li key={d.name}>{d.name}</li>
        ) : (
          <li key={d.name}>
            {d.name}: {value}
          </li>
        );
      });

      let links = linkTimeSeries;
      if (formattedReturn.oscar_id !== null) {
        links = (
          <>
            {linkTimeSeries}
            <br />
            <hr />
            <LinkOSCAR wigosid={formattedReturn.oscar_id} />
          </>
        );
      }
      setContent(
        <ul className="popup-station-properties">
          {htmlContent}
          {links}
        </ul>
      );
    })
    .catch(error => {
      console.log("ERROR API");
      console.log(error);
    });
}

export function fetchSynopSimplePopup(
  props,
  periodsAsQP,
  multiplier,
  linkTimeSeries,
  setContent
) {
  fetchData(
    `${URL_STATION}?network=${props.fileType}&id=${props.stationObject.station_id}`
  )
    .then(response => {
      let dataFeature;
      if (props.selectedReport === "availability") {
        dataFeature = pickProperties(props.stationObject, [
          "nr_received",
          "nr_expected"
        ]);
      } else {
        dataFeature = pickProperties(props.stationObject, [
          "avg_bg_dep",
          "std_bg_dep",
          "rms"
        ]);
      }

      props.onSetSelectedStation({
        name: response.data.name,
        wigosid: response.data.wigosid,
        in_oscar: response.data.in_oscar,
        elevation: response.data.elevation
      });

      const dataAPI = pickProperties(response.data, ["name", "wigosid"]);

      const formattedReturn = mergeDataSources(
        props.selectedReport,
        dataFeature,
        dataAPI,
        multiplier,
        props.selectedPeriodType
      );
      let htmlContent = formattedReturn.data.map(d => {
        if (d.name === "NWP-ID") {
          return check_tsi_wsi(d);
        }
        return (
          <li key={d.name}>
            {d.name}: {d.value}
          </li>
        );
      });

      let links = linkTimeSeries;
      if (response.data.name !== "Unknown") {
        links = (
          <>
            {linkTimeSeries}
            <br />
            <hr />
            <LinkOSCAR wigosid={formattedReturn.oscar_id} />
          </>
        );
      }

      setContent(
        <ul className="popup-station-properties">
          {htmlContent}
          {links}
        </ul>
      );
    })
    .catch(error => {
      console.log("ERROR API");
      console.log(error);
    });
}

export function fetchSynopAlertCombinedPopup(
  props,
  report,
  dateUntil,
  center,
  multiplier,
  linkTimeSeries,
  setContent
) {
  const promise1 = fetchData(
    `${URL_SYNOP_AGGREGATED_OBSERVATIONS_TIMESERIES}/?baseline=${props.baseline}&report=${report}&period_type=daily&station=${props.stationObject.station_id}&variable=${props.selectedVariable}&date=${dateUntil}&center=${center}&nb_days=4`
  );

  const promise2 = fetchData(
    `${URL_STATION}?network=${props.fileType}&id=${props.stationObject.station_id}`
  );
  Promise.all([promise1, promise2])
    .then(function(responses) {
      const obsData = responses[0].data;
      const stationInfo = responses[1].data;

      props.onSetSelectedStation({
        name: stationInfo.name,
        wigosid: stationInfo.wigosid,
        in_oscar: stationInfo.in_oscar,
        elevation: stationInfo.elevation
      });

      const valuesByCenter = [];
      allCenters.forEach(v => {
        valuesByCenter.push({
          name: v,
          values: []
        });
      });

      obsData.forEach(v => {
        const element = valuesByCenter.filter(c => {
          return c.name === v.center;
        })[0];
        if (v.avg_bg_dep !== null) {
          element.values.push(Math.abs(v.avg_bg_dep * multiplier));
        }
      });

      let labelID = "WIGOS-ID";

      if (stationInfo.name === "Unknown") {
        labelID = "TSI";
        if (stationInfo.wigosid.includes("-")) {
          labelID = "WSI";
        }
      }

      const htmlPart1 = (
        <>
          <li key={1}>Name: {stationInfo.name}</li>
          <li key={2}>
            {labelID}: {stationInfo.wigosid}
          </li>
        </>
      );

      if (
        props.selectedReport === "quality" &&
        props.selectedVariable === "58"
      ) {
        valuesByCenter.forEach((d, i) => {
          if (d.name === "NCEP") {
            valuesByCenter.splice(i, 1);
          }
        });
      }

      const htmlPart2 = valuesByCenter.map(d => {
        const meanValue =
          d.values.length === 0
            ? "No data"
            : Math.round(mean(d.values) * 1000) / 1000;
        const nbObs =
          d.values.length === 0 ? null : `(${d.values.length} daily values)`;
        return (
          <li key={d.name}>
            {d.name}: {meanValue} {nbObs}
          </li>
        );
      });

      const htmlContent = (
        <>
          {htmlPart1}
          {htmlPart2}
        </>
      );

      let links = linkTimeSeries;
      if (stationInfo.name !== "Unknown") {
        links = (
          <>
            {linkTimeSeries}
            <br />
            <hr />
            <LinkOSCAR wigosid={stationInfo.wigosid} />
          </>
        );
      }

      setContent(
        <ul className="popup-station-properties">
          {htmlContent}
          {links}
        </ul>
      );
    })
    .catch(error => console.log(`Error in executing ${error}`));
}

export function fetchSynopAlertSimplePopup(
  props,
  multiplier,
  linkTimeSeries,
  setContent
) {
  fetchData(
    `${URL_STATION}?network=${props.fileType}&id=${props.stationObject.station_id}`
  )
    .then(response => {
      props.onSetSelectedStation({
        name: response.data.name,
        wigosid: response.data.wigosid,
        in_oscar: response.data.in_oscar,
        elevation: response.data.elevation
      });

      let labelID = "WIGOS-ID";

      if (response.data.name === "Unknown") {
        labelID = "TSI";
        if (response.data.wigosid.includes("-")) {
          labelID = "WSI";
        }
      }
      const htmlContent = (
        <>
          <li key={1}>Name: {response.data.name}</li>
          <li key={2}>
            {labelID}: {response.data.wigosid}
          </li>
          <li key={3}>
            Value:{" "}
            {Math.round(props.stationObject.avg_bg_dep * multiplier * 1000) /
              1000}
          </li>
          <li key={4}>#Daily values: {props.stationObject.nb_obs}</li>
        </>
      );

      let links = linkTimeSeries;
      if (response.data.name !== "Unknown") {
        links = (
          <>
            {linkTimeSeries}
            <br />
            <hr />
            <LinkOSCAR wigosid={response.data.wigosid} />
          </>
        );
      }

      setContent(
        <ul className="popup-station-properties">
          {htmlContent}
          {links}
        </ul>
      );
    })
    .catch(error => {
      console.log("ERROR API");
    });
}
